<div class="container-fluid" style="max-width: 1400px;">
    <h1 class="m-3 display-6 vm-container-header"><i class="bi bi-diagram-3 me-2"></i>852 ZA Code Maps</h1>

    <div class="pb-5">
        <div class="m-3 p-2 bg-body-light rounded-3 shadow">
            <div class="m-2">
                <!-- Customer -> trading partner cascade -->
                <form>
                    <div class="mb-3 row align-items-center">
                        <div class="col-sm-auto">
                            <select (change)="onCustomerChange($event)" aria-label="Select a customer" class="form-select">
                                <option value="">Select a Customer</option>
                                @for (entity of customers; track entity.id) {
                                    <option value="{{ entity.id }}">{{ entity.name }}</option>
                                }
                            </select>
                        </div>
                        <div class="col-sm-auto">
                            <select (change)="onTradingPartnerChange($event)" aria-label="Select a trading partner"
                                    class="form-select" [disabled]="!customerId()">
                                <option value="">Select a Trading Partner</option>
                                @for (tp of tradingPartners(); track tp.id) {
                                    <option value="{{ tp.id }}">{{ tp.name }}</option>
                                }
                            </select>
                        </div>
                    </div>
                </form>

                @if (!customerId()) {
                    <div class="card text-center bg-info-subtle border border-info rounded p-3 m-5">
                        <div class="card-body">
                            <i class="bi bi-info-circle-fill me-2"></i>Select a customer and trading partner to edit its ZA code map.
                        </div>
                    </div>
                } @else if (!tradingPartnerId()) {
                    <div class="card text-center bg-info-subtle border border-info rounded p-3 m-5">
                        <div class="card-body">
                            <i class="bi bi-info-circle-fill me-2"></i>Select a trading partner.
                        </div>
                    </div>
                }

                @if (tradingPartnerId() && partnerRows().length > 0) {
                    <!-- DC override indicator -->
                    <div class="border rounded p-2 mb-3 bg-body-tertiary">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <span class="fw-semibold"><i class="bi bi-signpost-split me-1"></i>Distribution-center overrides</span>
                            <div class="btn-group btn-group-sm">
                                <button type="button" class="btn btn-outline-primary" (click)="openOverrideEditor()">
                                    <i class="bi bi-plus-circle me-1"></i>Add override
                                </button>
                                <button type="button" class="btn btn-outline-dark" (click)="openAllOverrides()">
                                    <i class="bi bi-list-ul me-1"></i>View all overrides
                                </button>
                            </div>
                        </div>
                        @if (overrideIndicators().length === 0) {
                            <span class="text-muted small">No DCs override this partner map — every DC follows it.</span>
                        } @else {
                            <div class="d-flex flex-wrap gap-2">
                                @for (ind of overrideIndicators(); track ind.distributionCenterId) {
                                    <span class="badge text-bg-secondary d-inline-flex align-items-center gap-1">
                                        {{ ind.distributionCenterName }} ({{ ind.overrideCount }})
                                        <i class="bi bi-pencil ms-1" role="button" title="Edit overrides"
                                           (click)="openOverrideEditor(ind.distributionCenterId)"></i>
                                        <i class="bi bi-arrow-counterclockwise" role="button" title="Revert all"
                                           (click)="revertAllForDc(ind)"></i>
                                    </span>
                                }
                            </div>
                        }
                    </div>

                    <!-- Bulk actions -->
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <div class="btn-group btn-group-sm">
                            <button type="button" class="btn btn-outline-dark" [disabled]="selectedCodes().size === 0"
                                    (click)="bulkSet('none')">Set selected → None</button>
                            <button type="button" class="btn btn-outline-dark" [disabled]="selectedCodes().size === 0"
                                    (click)="bulkSet('passthrough')">Set selected → Passthrough</button>
                        </div>
                        <div class="d-flex gap-2">
                            <span [title]="partnerMapDirty() ? 'Save the partner map before reprocessing' : ''">
                                <button type="button" class="btn btn-outline-primary btn-sm"
                                        [disabled]="savingPartnerMap() || partnerMapDirty()"
                                        (click)="reprocessPartnerFiles()">
                                    <i class="bi bi-arrow-repeat me-1"></i>Reprocess this partner's 852 files
                                </button>
                            </span>
                            <button type="button" class="btn btn-primary btn-sm" [disabled]="savingPartnerMap()"
                                    (click)="savePartnerMap()">
                                @if (savingPartnerMap()) { <span class="spinner-border spinner-border-sm me-1"></span> }
                                Save partner map
                            </button>
                        </div>
                    </div>

                    <!-- Partner-map grid -->
                    <table class="table table-sm table-striped table-hover align-middle">
                        <thead>
                        <tr>
                            <th scope="col" style="width: 2.5rem;">
                                <input type="checkbox" class="form-check-input"
                                       [checked]="isAllCodesSelected()"
                                       (change)="toggleSelectAllCodes()"
                                       aria-label="Select all ZA codes">
                            </th>
                            <th scope="col">ZA Code</th>
                            <th scope="col" style="width: 10rem;">State</th>
                            <th scope="col">Formula</th>
                            <th scope="col" style="width: 10rem;">Effective From</th>
                            <th scope="col" style="width: 10rem;">Effective To</th>
                            <th scope="col">Description</th>
                        </tr>
                        </thead>
                        <tbody class="table-group-divider">
                            @for (row of partnerRows(); track row.zaCode) {
                                <tr [class.has-config]="row.state !== 'passthrough'">
                                    <td>
                                        <input type="checkbox" class="form-check-input"
                                               [checked]="selectedCodes().has(row.zaCode)"
                                               (change)="toggleCode(row.zaCode)">
                                    </td>
                                    <td class="fw-semibold">{{ row.zaCode }}</td>
                                    <td>
                                        <select class="form-select form-select-sm" [value]="row.state"
                                                (change)="setState(row.zaCode, $any($event.target).value)">
                                            <option value="passthrough">Passthrough</option>
                                            <option value="none">None</option>
                                            <option value="formula">Formula</option>
                                        </select>
                                    </td>
                                    <td>
                                        @if (row.state === 'formula') {
                                            <input type="text" class="form-control form-control-sm" placeholder="e.g. QM + QL"
                                                   [value]="row.formula" (input)="setFormula(row.zaCode, $any($event.target).value)">
                                        } @else {
                                            <span class="text-muted small">{{ row.state === 'none' ? 'None (reported gap)' : 'Passthrough' }}</span>
                                        }
                                    </td>
                                    <td>
                                        <input type="date" class="form-control form-control-sm" [value]="row.effFrDate"
                                               (change)="setEffFrDate(row.zaCode, $any($event.target).value)">
                                    </td>
                                    <td>
                                        <input type="date" class="form-control form-control-sm" [value]="row.effToDate"
                                               (change)="setEffToDate(row.zaCode, $any($event.target).value)">
                                    </td>
                                    <td class="text-muted small">{{ row.description }}</td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }
            </div>
        </div>

        <!-- Override editor -->
        <ng-template #overrideModal>
            <div class="modal-header">
                <h1 class="modal-title fs-5">Distribution-center overrides — {{ selectedTradingPartnerName() }}</h1>
                <button (click)="overrideModalRef.dismiss()" type="button" class="btn-close" aria-label="Close"></button>
            </div>
            <div class="modal-body container-fluid">
                <div class="row g-3">
                    <!-- DC multi-select -->
                    <div class="col-4 d-flex flex-column">
                        <h6>Apply to distribution centers ({{ selectedDcIds().size }})</h6>
                        <div class="list-group overflow-auto border rounded" style="height: 50vh;">
                            @for (dc of dcList(); track dc.id) {
                                <button type="button" class="list-group-item list-group-item-action"
                                        [class.active]="selectedDcIds().has(dc.id)" (click)="toggleDc(dc.id)">
                                    {{ dc.name }}
                                </button>
                            }
                            @empty {
                                <div class="list-group-item text-muted text-center">No distribution centers</div>
                            }
                        </div>
                    </div>

                    <!-- Override code grid -->
                    <div class="col-8 d-flex flex-column">
                        <h6>Override codes <span class="text-muted small">(leave as Inherit to follow the partner map)</span></h6>
                        <div class="overflow-auto border rounded" style="height: 50vh;">
                            <table class="table table-sm table-striped table-hover align-middle mb-0">
                                <thead class="sticky-top bg-body">
                                <tr>
                                    <th scope="col">ZA Code</th>
                                    <th scope="col" style="width: 9rem;">State</th>
                                    <th scope="col">Formula</th>
                                </tr>
                                </thead>
                                <tbody>
                                    @for (row of overrideRows(); track row.zaCode) {
                                        <tr [class.has-config]="row.state !== 'inherit'">
                                            <td class="fw-semibold">{{ row.zaCode }}</td>
                                            <td>
                                                <select class="form-select form-select-sm" [value]="row.state"
                                                        (change)="setOverrideState(row.zaCode, $any($event.target).value)">
                                                    <option value="inherit">Inherit</option>
                                                    <option value="passthrough">Passthrough</option>
                                                    <option value="none">None</option>
                                                    <option value="formula">Formula</option>
                                                </select>
                                            </td>
                                            <td>
                                                @if (row.state === 'formula') {
                                                    <input type="text" class="form-control form-control-sm" placeholder="e.g. QM + QL"
                                                           [value]="row.formula" (input)="setOverrideFormula(row.zaCode, $any($event.target).value)">
                                                } @else {
                                                    <span class="text-muted small">&mdash;</span>
                                                }
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <div class="modal-footer">
                <button [disabled]="savingOverrides()" type="button" class="btn btn-primary" (click)="saveOverrides()">
                    @if (savingOverrides()) { <span class="spinner-border spinner-border-sm me-1"></span> }
                    Apply overrides
                </button>
                <button (click)="overrideModalRef.dismiss()" type="button" class="btn btn-secondary">Close</button>
            </div>
        </ng-template>

        <!-- All overrides -->
        <ng-template #allOverridesModal>
            <div class="modal-header">
                <h1 class="modal-title fs-5">All overrides</h1>
                <button (click)="allOverridesModalRef.dismiss()" type="button" class="btn-close" aria-label="Close"></button>
            </div>
            <div class="modal-body container-fluid">
                @if (allOverrides().length === 0) {
                    <div class="text-muted text-center p-3">No overrides for this customer.</div>
                } @else {
                    <table class="table table-sm table-striped table-hover align-middle">
                        <thead>
                        <tr>
                            <th scope="col">Trading Partner</th>
                            <th scope="col">Distribution Center</th>
                            <th scope="col">ZA Code</th>
                            <th scope="col">Formula</th>
                            <th scope="col" class="text-center">Revert</th>
                        </tr>
                        </thead>
                        <tbody class="table-group-divider">
                            @for (o of allOverrides(); track o.id) {
                                <tr>
                                    <td>{{ o.tradingPartnerName }}</td>
                                    <td>{{ o.distributionCenterName }}</td>
                                    <td class="fw-semibold">{{ o.zaCode }}</td>
                                    <td>{{ o.formula === o.zaCode ? 'Passthrough' : o.formula }}</td>
                                    <td class="text-center">
                                        <i class="btn btn-sm btn-outline-danger bi bi-arrow-counterclockwise"
                                           title="Revert to partner map" (click)="revertOverride(o)"></i>
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }
            </div>
            <div class="modal-footer">
                <button (click)="allOverridesModalRef.dismiss()" type="button" class="btn btn-secondary">Close</button>
            </div>
        </ng-template>

        <app-modal-confirm #confirmationModal></app-modal-confirm>
    </div>
</div>
